import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../framework/redux/hooks";
import { useAsyncThunkAction } from "../../../hooks/framework/useAsyncThunkAction";
import { handleApiError } from "../../../services/baseService";
import { fetchMyVehicles } from "../../../framework/redux/slices/vehicleSlice";
import { LoadingIndicator } from "../../framework/loadingIndicator";
import { TransportOrderSaveData } from "../../../models/transport/transportOrderSaveData";
import { OrderForm } from "./orderForm";
import { getCustomers } from "../../../framework/redux/slices/customerSlice";
import { getOrderDetails } from "../../../framework/redux/slices/transportOrderDetailsSlice";
import { SerializedError } from "@reduxjs/toolkit";
import { EmployeeSessionItemDto } from "../../../models/transport/employee";

export const OrderFormMain = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const myVehicles = useAppSelector(({ vehicle }) => vehicle.myVehicles);
    const customers =useAppSelector(({ customer }) => customer.customers);
    const [transportOrderSaveData, setTransportOrderSaveData] =
        useState<TransportOrderSaveData>();

    const order = useAppSelector((state) => state.transportOrderDetails.order);
    const { orderId } = useParams();

    const employees: EmployeeSessionItemDto[] = [
        {
            id: useAppSelector((state) => state.session.id),
            fullName: useAppSelector((state) => state.session.fullName)
        }
    ];

    useEffect(() => {
        if (orderId) {
            const promise = dispatch(getOrderDetails({ orderId }));
            promise.unwrap().catch((e: SerializedError) => {
                if (e?.name !== "AbortError") navigate("/");
            });
            return () => {
                promise.abort();
            };
        }
    }, [orderId, dispatch, navigate]);

    useAsyncThunkAction(
        () => fetchMyVehicles(),
        { onError: handleApiError },
        []
    );

    useAsyncThunkAction(
        () => getCustomers(),
        { onError: handleApiError },
        []
    );

    const isEdit = !!orderId;
    const editItemReady = order?.id === orderId;

    useEffect(() => {
        // Init new order
        // When not initalized and id is not present
        if (!isEdit && !transportOrderSaveData) {
            setTransportOrderSaveData({
                id: "",
                billingCustomerId: null,
                receiverCustomerId: null,
                senderCustomerId: null,
                employeeId: null,
                scheduledStartDateTime: null,
                scheduledEndDateTime: null,
                vehicleId: null,
                driverComments: "",
            });
        }
    }, [isEdit]);

    useEffect(() => {
        // Init edit order
        // When id is present and current match with id
        // And order has been loaded
        if (isEdit && editItemReady && order) {
            const {
                id,
                billingCustomerId,
                receiverCustomerId,
                senderCustomerId,
                employeeId,
                scheduledStartDateTime,
                scheduledEndDateTime,
                vehicleId,
                driverComments,
            } = order;
            setTransportOrderSaveData({
                id,
                billingCustomerId,
                receiverCustomerId,
                senderCustomerId,
                employeeId,
                scheduledStartDateTime,
                scheduledEndDateTime,
                vehicleId,
                driverComments,
            });
        }
    }, [orderId, order]);

    if (!transportOrderSaveData || (isEdit && !editItemReady))
        return <LoadingIndicator />;

    return (
        <OrderForm
            order={transportOrderSaveData}
            vehicles={myVehicles}
            customers={customers}
            employees={employees}
        />
    );
};
